import * as React from 'react';
import FormSection, {FormSectionProps} from './FormSection';
import {createStandardAction, getType, ActionType} from 'typesafe-actions';
import {Set} from 'immutable';
import {createStyles, withStyles, WithStyles} from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import ChevronDoubleUp from 'mdi-material-ui/ChevronDoubleUp';
import ChevronDoubleDown from 'mdi-material-ui/ChevronDoubleDown';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import {FormContext} from '../FormContext/FormContextProvider';
const actions = {
  toggleOpen: createStandardAction('TOGGLE_OPEN')<{key: React.Key}>(),
  expandAll: createStandardAction('EXPAND_ALL')(),
  collapseAll: createStandardAction('COLLAPSE_ALL')(),
};

type FormState =
  | {
      openMode: 'multiple';
      closedSections: Set<React.Key>;
    }
  | {
      openMode: 'single';
      openSectionKey: React.Key;
    };

const initialStateClosed: FormState = {openMode: 'single', openSectionKey: null};

const initialStateOpen: FormState = {openMode: 'multiple', closedSections: Set<React.Key>()};

function reducer(state: FormState, action: ActionType<typeof actions>): FormState {
  switch (action.type) {
    case getType(actions.toggleOpen):
      const {key} = action.payload;
      if (state.openMode === 'single') {
        return {...state, openSectionKey: state.openSectionKey === key ? null : key};
      } else {
        return {
          ...state,
          closedSections: state.closedSections.has(key)
            ? state.closedSections.remove(key)
            : state.closedSections.add(key),
        };
      }
    case getType(actions.expandAll):
      return initialStateOpen;
    case getType(actions.collapseAll):
      return initialStateClosed;
    default:
      throw new Error();
  }
}

function isOpen(key: React.Key, state: FormState) {
  if (state.openMode === 'single') {
    return key === state.openSectionKey;
  } else {
    return !state.closedSections.has(key);
  }
}

export interface FormSectionsBaseProps {
  title?: React.ReactNode;
  initialState?: FormState | 'closed' | 'open';
}

const FormSectionsBase: React.FunctionComponent<FormSectionsBaseProps & WithStyles<typeof styles>> = ({
  title,
  initialState: initialStateProp = 'closed',
  children,
  classes: {title: titleClass},
}) => {
  let initialState: FormState;
  switch (initialStateProp) {
    case 'closed':
      initialState = initialStateClosed;
      break;
    case 'open':
      initialState = initialStateOpen;
      break;
    default:
      initialState = initialStateProp;
  }
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const {
    errorMeta: {sectionWithError},
  } = React.useContext(FormContext);
  React.useEffect(() => {
    if (sectionWithError && !isOpen(sectionWithError, state)) {
      dispatch(actions.toggleOpen({key: sectionWithError}));
    }
  }, [sectionWithError]);

  return (
    <>
      <div className={titleClass}>
        <Typography variant="h5">{title}</Typography>
        <div>
          {state.openMode === 'multiple' ? (
            <Tooltip title="Collapse all">
              <IconButton id="cla-btn-collapse-all" onClick={() => dispatch(actions.collapseAll())}>
                <ChevronDoubleUp id="cla-icon-collapse-all" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Expand all">
              <IconButton id="cla-btn-expand-all" onClick={() => dispatch(actions.expandAll())}>
                <ChevronDoubleDown id="cla-icon-expand-all" />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
      {React.Children.map(children, child => {
        if (React.isValidElement<FormSectionProps>(child) && child.type === FormSection) {
          return React.cloneElement<FormSectionProps>(child, {
            onToggleOpen: () => dispatch(actions.toggleOpen({key: child.key})),
            open: isOpen(child.key, state),
          });
        }
        return child;
      })}
    </>
  );
};

const styles = createStyles({
  title: {
    display: 'flex',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
});

export default withStyles(styles)(FormSectionsBase);
